import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia, StaffExample } from "@/components/sn/SNShell";
import { Notation } from "@/components/Notation";

export default function StaffNotation5() {
  const { lesson, course, accent } = findLesson("staff-notation", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN5-Time-Signature-Reading-Animated.mp4"
        caption="Reading 4/4 — five rhythmic patterns in one bar"
        hint="≈ 28 sec · watch first"
      />
      <Folio number="I" title="The time signature — two numbers, two instructions">
        <p>
          With note values covered at the micro level, Jason brings in the <strong>macro</strong>:
          the time signature. It sits at the start of every piece of music, before any note is
          played. It looks like a fraction, but it is not a fraction — it is two stacked
          instructions.
        </p>
        <Glossarium>
          <p>
            <strong>Top number</strong> — the number of beats in a bar (also called a measure).
            This is how many beats you count before the bar repeats.
          </p>
          <p>
            <strong>Bottom number</strong> — which type of note represents one beat. 4 means a
            quarter note (crotchet). 8 means an eighth note (quaver). 2 means a half note (minim).
          </p>
        </Glossarium>
        <p>
          So <strong>4/4</strong> means: four beats per bar, and each beat is a quarter note.
          Jason puts it as an equation: <em>4/4 = 4 × 1/4</em>. "Math students will understand
          this," he says. In every bar of a 4/4 song, all the note values must add up to exactly
          four crotchets. That is the rule.
        </p>
      </Folio>

      <Folio number="II" title="Note values are just numbers with pictures">
        <p>
          Here is Jason's key insight about how to think about filling a bar: <em>"Note values
          are maths numbers — they are just numbers which have a visual or a pictorial sign
          about them."</em> Once you see them as numbers, everything becomes arithmetic.
        </p>
        <p>
          In a 4/4 bar, the total is 4. Can you fill it with four quarter notes? Yes:{" "}
          1 + 1 + 1 + 1 = 4. Can you use two quarter notes and one half note?{" "}
          1 + 1 + 2 = 4. Can the half note go first? 2 + 1 + 1 = 4. Can it go in the middle?{" "}
          1 + 2 + 1 = 4. Jason runs through these combinations one by one, then asks:
        </p>
        <p>
          <em>"By that argument can we not do two minims? Why not? 2 plus 2 equals 4."</em>{" "}
          Yes — you can. A dotted minim and a crotchet? 3 + 1 = 4. Yes — you can. Or flip
          it: 1 + 3 = 4.
          Or just play the egg — a single semibreve for all four beats.
        </p>
        <p>
          The combinations multiply further when you add rests into the party — and you can.
          A crotchet, then a rest, then two more crotchets still adds up to 4. A minim rest
          followed by two crotchets: 2 + 1 + 1 = 4. The rest is as valid as any note.
        </p>
      </Folio>

      <StaffExample caption="Three ways to fill a 4/4 bar — four quarter notes, then a half plus two quarters, then a dotted half plus a quarter.">
        <Notation
          measures={[
            {
              chords: [
                { keys: ["C4"], duration: "q" },
                { keys: ["E4"], duration: "q" },
                { keys: ["G4"], duration: "q" },
                { keys: ["C5"], duration: "q" },
              ],
            },
            {
              chords: [
                { keys: ["C4"], duration: "h" },
                { keys: ["E4"], duration: "q" },
                { keys: ["G4"], duration: "q" },
              ],
            },
            {
              chords: [
                { keys: ["C4"], duration: "hd" },
                { keys: ["G4"], duration: "q" },
              ],
            },
          ]}
          timeSig="4/4"
          clef="treble"
          width={720}
          height={150}
        />
      </StaffExample>

      <Folio number="III" title="Beaming quavers — what looks wrong is wrong">
        <p>
          When you add eighth notes (quavers) to the bar, you cannot simply draw each one
          with its own separate flag. Jason is clear about this: "This is absolutely wrong.
          Rather you have to beam them." Beamed quavers are connected by a horizontal bar
          across the top of their stems.
        </p>
        <p>
          The convention is to beam quavers within a beat group. In 4/4, you beam the two
          quavers that together make up one beat. Four beamed quavers (two beats) may group
          together depending on the phrase. Semiquavers get a double beam. The visual grouping
          is the message: it tells the player exactly how many beats have elapsed.
        </p>
        <Marginalia>
          Beaming applies to quavers, semiquavers, and tuplets. In instrumental writing it is
          not optional — a trained reader sees unbeamed quavers in sequence and immediately
          recognises something is off. (Vocal parts are the exception: one note per syllable.)
          The beam is the signal that these notes belong to the same beat group.
        </Marginalia>
      </Folio>

      <Folio number="IV" title="Counting — one and, one e and a">
        <p>
          To read rhythm accurately, Jason teaches counting aloud with syllables. The beat
          number is always the downbeat. Subdivisions add syllables between the numbers.
        </p>
        <Glossarium>
          <p>
            <strong>Quarter notes:</strong> "1, 2, 3, 4" — one syllable per beat. Simple.
          </p>
          <p>
            <strong>Eighth notes (quavers):</strong> "1 and, 2 and, 3 and, 4 and" — the beat
            splits in two. "And" lands precisely at the midpoint of each beat. You are dividing
            the beat by two. Notes can fall on the number (downbeat) or on the "and" (upbeat).
          </p>
          <p>
            <strong>Sixteenth notes (semiquavers):</strong> "1 e and a, 2 e and a, 3 e and a,
            4 e and a" — each beat divides into four equal parts. Say the syllables at an
            even pace. A note that falls only on "and a" is on the second half of the beat,
            divided in two.
          </p>
        </Glossarium>
        <p>
          You can mix note values and rests within any beat. A quaver rest followed by a quaver
          note: count "1 and" — silence on 1, sound on "and." Four quavers followed by a minim
          rest: count "1 and 2 and" out loud, then stay silent through "3 and 4 and" — two beats
          of sound, two of rest, four in the bar. Practice these combinations by clapping along
          to your counting.
        </p>
      </Folio>

      <StaffExample caption="Beamed quavers in 4/4 — two per beat, four pairs filling the bar.">
        <Notation
          measures={[
            {
              chords: [
                { keys: ["C4"], duration: "8" },
                { keys: ["E4"], duration: "8" },
                { keys: ["G4"], duration: "8" },
                { keys: ["C5"], duration: "8" },
                { keys: ["G4"], duration: "8" },
                { keys: ["E4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["E4"], duration: "8" },
              ],
            },
          ]}
          timeSig="4/4"
          clef="treble"
          width={520}
          height={150}
        />
      </StaffExample>

      <Engraving
        src="/notes/staff-notation/finals/05-time-signatures-page1.png"
        caption="Jason's note: 4/4 = 4 × ¼ = 4 crotchets. Top number = beats per bar. Bottom number = which note type counts as one beat."
      />
    </LessonShell>
  );
}
